import { createFileRoute, Link } from "@tanstack/react-router";
import { AppNav } from "@/components/AppNav";

export const Route = createFileRoute("/termos")({
  head: () => ({
    meta: [{ title: "Termos e Condições de Uso — Busca Blindados" }],
  }),
  component: TermosPage,
});

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-lg text-foreground sm:text-xl">
        {n}. {title}
      </h2>
      <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground/80">{children}</div>
    </section>
  );
}

function TermosPage() {
  return (
    <div className="min-h-screen text-foreground">
      <AppNav />
      <main className="mx-auto max-w-3xl px-6 pb-32 pt-6">
        <header>
          <h1 className="font-display text-3xl text-foreground sm:text-4xl">
            Termos e Condições de Uso
          </h1>
          <p className="mt-2 text-xs text-muted-foreground">
            Vigência a partir de 19 de maio de 2026
          </p>
        </header>

        <Section n="1" title="Objeto">
          <p>
            Os presentes Termos regulam o uso da plataforma Busca Blindados ("Serviço"), que
            agrega, indexa e organiza anúncios públicos de veículos publicados por terceiros em
            sites próprios e abertos na internet, oferecendo aos Usuários ferramentas de busca,
            comparação, alertas e funcionalidades premium mediante assinatura.
          </p>
        </Section>

        <Section n="2" title="Natureza do Serviço — Aviso Fundamental">
          <p>
            O Busca Blindados{" "}
            <span className="font-semibold text-foreground">
              NÃO É vendedor, distribuidor, intermediário comercial, consignatário ou
              representante de qualquer veículo anunciado.
            </span>{" "}
            Não negocia, não recebe pagamento, não emite nota fiscal, não realiza vistoria, não
            garante a veracidade, atualidade, disponibilidade, condição, procedência ou licitúde
            dos veículos ou anúncios listados. O Busca Blindados equipara-se a um mecanismo de
            busca especializado.
          </p>
        </Section>

        <Section n="3" title="Cadastro e Conta">
          <p>
            <span className="font-medium text-foreground/90">3.1.</span> Cadastro gratuito,
            exigindo e-mail válido, idade mínima de 18 anos e capacidade civil.
          </p>
          <p>
            <span className="font-medium text-foreground/90">3.2.</span> O Usuário é responsável
            pela veracidade dos dados, sigilo da senha e por toda atividade em sua conta.
          </p>
          <p>
            <span className="font-medium text-foreground/90">3.3.</span> O Busca Blindados pode
            suspender contas com indícios de fraude, automação não autorizada, scraping reverso
            da plataforma, abuso ou descumprimento destes Termos.
          </p>
        </Section>

        <Section n="4" title="Planos e Assinatura">
          <p>
            <span className="font-medium text-foreground/90">4.1.</span> Plano gratuito (com
            limites) e planos de assinatura paga com alertas avançados, buscas, comparativos e
            notificações.
          </p>
          <p>
            <span className="font-medium text-foreground/90">4.2.</span> Preços descritos em{" "}
            <Link to="/planos" className="text-foreground underline underline-offset-2 hover:text-primary">
              buscablindados.com/planos
            </Link>
            . Reajustes comunicados com 30 dias de antecedência.
          </p>
          <p>
            <span className="font-medium text-foreground/90">4.3.</span> Cobrança recorrente.
            Cancelamento produz efeitos ao término do ciclo pago, sem reembolso proporcional,
            salvo defeito do Serviço ou direito de arrependimento.
          </p>
          <p>
            <span className="font-medium text-foreground/90">4.4.</span> Inadimplência suspende
            o acesso premium.
          </p>
        </Section>

        <Section n="5" title="Direitos de Uso (Licença)">
          <p>
            Licença pessoal, gratuita (free) ou onerosa (premium), não exclusiva, intransferível
            e revogável, para uso apenas de consulta pessoal e de boa-fé.
          </p>
          <p className="font-medium text-foreground/90">É proibido:</p>
          <ul className="ml-4 list-disc space-y-1.5">
            <li>Scrape, crawl, mirror, framing ou extração automatizada do conteúdo do Busca Blindados;</li>
            <li>Revenda ou redistribuição;</li>
            <li>Engenharia reversa;</li>
            <li>Treinamento de modelos de IA sem autorização escrita;</li>
            <li>Qualquer ato que prejudique a integridade ou segurança do Serviço.</li>
          </ul>
        </Section>

        <Section n="6" title="Propriedade Intelectual">
          <p>
            <span className="font-medium text-foreground/90">6.1.</span> A marca, o software, o
            design, os algoritmos de busca e as compilações editoriais do Busca Blindados são
            propriedade exclusiva da plataforma, protegidos pela LPI, LDA e Lei de Software.
          </p>
          <p>
            <span className="font-medium text-foreground/90">6.2.</span> Os anúncios indexados
            pertencem aos respectivos anunciantes e revendas, apresentados em formato indicial
            com link direto à fonte. O Busca Blindados não reivindica titularidade sobre o
            conteúdo de terceiros.
          </p>
        </Section>

        <Section n="7" title="Conteúdo de Terceiros — Limitação de Responsabilidade">
          <p>
            <span className="font-medium text-foreground/90">7.1.</span> O Serviço é mero
            indexador e redirecionador. Não verificamos veracidade, regularidade ou condição dos
            veículos.
          </p>
          <p>
            <span className="font-medium text-foreground/90">7.2.</span> Preços, descrições,
            fotos e disponibilidade são de responsabilidade exclusiva do anunciante original.
          </p>
          <p>
            <span className="font-medium text-foreground/90">7.3.</span> Em caso de divergência,
            prevalece sempre a informação do site de origem.
          </p>
          <p>
            <span className="font-medium text-foreground/90">7.4.</span> O Busca Blindados não
            se responsabiliza por anúncios falsos, fraudes, prejuízos em transações com
            terceiros, indisponibilidade do site de origem ou tributação e transferência do
            veículo.
          </p>
        </Section>

        <Section n="8" title="Limitação Geral de Responsabilidade">
          <p>
            Na máxima extensão permitida pelo Direito brasileiro, a responsabilidade total do
            Busca Blindados fica limitada ao valor efetivamente pago pelo Usuário pela assinatura
            nos 12 meses anteriores ao evento. Não respondemos por danos indiretos, lucros
            cessantes, perda de oportunidade ou dano de imagem.
          </p>
        </Section>

        <Section n="9" title="Indenização">
          <p>
            O Usuário concorda em isentar e indenizar o Busca Blindados de qualquer perda ou
            despesa decorrente de uso indevido do Serviço, violação destes Termos, dos direitos
            de terceiros ou da legislação aplicável.
          </p>
        </Section>

        <Section n="10" title="Suspensão e Encerramento">
          <p>
            O Busca Blindados pode suspender ou encerrar o Serviço mediante aviso, sem ônus de
            continuidade, ressalvado o pro-rata da assinatura paga.
          </p>
        </Section>

        <Section n="11" title="Alterações dos Termos">
          <p>
            Comunicação com 15 dias de antecedência. Uso continuado após a vigência implica
            aceitação.
          </p>
        </Section>

        <Section n="12" title="Foro e Lei Aplicável">
          <p>
            Regido pela lei brasileira. Fica eleito o foro da Comarca de São Paulo/SP para
            dirimir quaisquer controvérsias decorrentes destes Termos.
          </p>
        </Section>

        <Section n="13" title="Aceite">
          <p>
            O Usuário declara ter lido, compreendido e aceito integralmente estes Termos ao
            clicar em "Li e aceito os Termos e Condições de Uso e a Política de Privacidade" no
            momento do cadastro.
          </p>
        </Section>
      </main>
    </div>
  );
}
